import { useCallback, useEffect, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "../components/AppShell";
import { NoPermission } from "../components/NoPermission";
import { NewUserDialog } from "../components/users/NewUserDialog";
import { ResetPasswordDialog } from "../components/users/ResetPasswordDialog";
import { UsersTable } from "../components/users/UsersTable";
import { isAdmin, useAuth } from "../context/AuthContext";
import { api, errorMessage } from "../lib/api";

function UsersAdmin({ me }) {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [resetting, setResetting] = useState(null);

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((err) => setError(errorMessage(err, "No se pudo cargar la lista de usuarios")));
  }, []);
  useEffect(load, [load]);

  const patch = async (u, changes, okMsg) => {
    setError("");
    try {
      const { data } = await api.patch(`/users/${u.id}`, changes);
      setUsers((list) => list.map((x) => (x.id === data.id ? data : x)));
      toast.success(okMsg);
    } catch (err) {
      const m = errorMessage(err, "No se pudo actualizar el usuario, intenta de nuevo");
      setError(m);
      toast.error(m);
      load();
    }
  };

  return (
    <>
      <section className="tr-fade-up flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="tr-eyebrow">Administración</div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#182235] sm:text-3xl">Usuarios</h1>
          <p className="text-sm text-[#5b667a]">Controla quién entra a la intranet y con qué rol.</p>
        </div>
        <NewUserDialog onCreated={(u) => { setUsers((l) => [...(l || []), u]); toast.success("Usuario creado"); }} />
      </section>
      {error && (
        <div role="alert" className="flex items-center gap-2 rounded-[14px] border border-[#f3c9c9] bg-[#fdf0ef] px-4 py-3 text-sm font-semibold text-[#b42318]" data-testid="users-error">
          <AlertCircle className="h-4 w-4 shrink-0" /> {error}
        </div>
      )}
      <section className="tr-card overflow-hidden" data-testid="users-section">
        <div className="hidden border-b border-[#e8edf5] bg-[#f8fafd] px-5 py-3 text-xs font-bold uppercase tracking-[0.04em] text-[#5b667a] lg:flex">
          <span className="w-[34%]">Nombre / Correo</span><span className="w-[30%]">Rol / Estado</span><span className="ml-auto">Acciones</span>
        </div>
        {!users ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#0f5ef7]" /></div>
        ) : (
          <UsersTable
            users={users}
            currentId={me.id}
            onToggle={(u) => patch(u, { active: !u.active }, u.active ? "Usuario desactivado" : "Usuario reactivado")}
            onRole={(u, role) => patch(u, { role }, "Rol actualizado")}
            onReset={setResetting}
          />
        )}
      </section>
      <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />
    </>
  );
}

export default function UsersPage() {
  const { user } = useAuth();
  return <AppShell>{isAdmin(user) ? <UsersAdmin me={user} /> : <NoPermission />}</AppShell>;
}
